'use client';

import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';

import {
  fetchCategorizedResults,
  fetchMixedResults,
} from '@/app/(main)/_components/search-modal/_services/search-modal-results.service';
import { AllSearchResults, SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import {
  clearSearchState,
  consumePreselectedFilter,
  removeSelectedSearch,
  setFetchedResults,
  setSearchTerm,
  useSearchState,
} from '@/store/search/search.store';
import { useDebounceFn } from '@/hooks/use-debounce.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { getLogger } from '@/logger/logger';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useSearchSource } from '@/app/(main)/_components/search-modal/_hooks/use-search-source.hook';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';

const EMPTY_RESULTS: AllSearchResults = {
  [SearchType.ALL]: { results: [], hasMore: false },
  [SearchType.PROCEDURE]: { results: [], hasMore: false },
  [SearchType.PROFESSIONAL]: { results: [], hasMore: false },
  [SearchType.PARTNER]: { results: [], hasMore: false },
};

export function useSearchModalFetch() {
  const searchState = useStore(useSearchState, (state) => state);
  const { source, citySlug, isLoading: searchSourceIsLoading } = useSearchSource();
  const { value: newSearchModalDesign } = useFeatureFlag('newSearchModalDesign');
  const analytics = getAnalyticsPlugin();

  const chooseInitialResults = (): AllSearchResults =>
    searchState.fetchedResults ?? EMPTY_RESULTS;

  const chooseShouldShowSearchResults = (): boolean =>
    searchState.fetchedResults
      ? Object.values(searchState.fetchedResults).some((r) => r.results.length > 0)
      : false;

  const [inputValue, setInputValue] = useState('');
  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [shouldShowSearchResults, setShouldShowSearchResults] = useState(chooseShouldShowSearchResults());
  const [shouldShowEmptyState, setShouldShowEmptyState] = useState(false);
  const [shouldShowErrorState, setShouldShowErrorState] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [searchResultIsLoading, setSearchResultIsLoading] = useState(false);
  const [allResults, setAllResults] = useState<AllSearchResults>(chooseInitialResults());

  const activeRequestId = useRef(0);
  const lastTrackedSearchTerm = useRef('');
  const debouncedSetInputValue = useDebounceFn(setInputValue, 400);

  const trackSearch = (term: string, hasAnyResult: boolean) => {
    if (lastTrackedSearchTerm.current === term) {
      return;
    }

    lastTrackedSearchTerm.current = term;

    analytics.track(
      hasAnyResult
        ? AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITH_RESULTS
        : AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITHOUT_RESULTS,
      {
        term,
        cidade: citySlug,
      },
    );
  };

  useEffect(() => {
    if (searchState.searchTerm) {
      setInputValue(searchState.searchTerm);
      setSearchDisplayValue(searchState.searchTerm);
    }
  }, [searchState.searchTerm]);

  useEffect(() => {
    const currentRequestId = ++activeRequestId.current;

    if (inputValue.length < 3) {
      clearSearchState();
      setAllResults(EMPTY_RESULTS);
      setShouldShowSearchResults(false);
      setShouldShowEmptyState(false);
      setShouldShowErrorState(false);
      setSearchResultIsLoading(false);
      return;
    }

    if (searchSourceIsLoading) {
      return;
    }

    if (searchState.searchTerm === inputValue) {
      return;
    }

    async function fetchResults() {
      setAllResults(EMPTY_RESULTS);
      setShouldShowSearchResults(false);
      setShouldShowEmptyState(false);
      setShouldShowErrorState(false);
      setSearchResultIsLoading(true);

      try {
        const results = newSearchModalDesign
          ? await fetchCategorizedResults(inputValue, { source, citySlug })
          : await fetchMixedResults(inputValue);

        if (currentRequestId !== activeRequestId.current) {
          return;
        }

        const hasAnyResult = Object.values(results).some((r) => r.results.length > 0);

        trackSearch(inputValue, hasAnyResult);

        if (hasAnyResult) {
          setShouldShowSearchResults(true);
          setShouldShowEmptyState(false);
        } else {
          setShouldShowEmptyState(true);
        }

        setFetchedResults(results);
        consumePreselectedFilter(results);
        setSearchTerm(inputValue);
        setAllResults(results);
      } catch (error) {
        getLogger('search-modal.fetch').error('Failed to fetch the search results', {
          search: { event: 'fetch_failed', request_sequence: currentRequestId },
          error,
        });
        if (currentRequestId === activeRequestId.current) {
          setSearchTerm(undefined);
          setShouldShowErrorState(true);
        }
      } finally {
        if (currentRequestId === activeRequestId.current) {
          setSearchResultIsLoading(false);
        }
      }
    }

    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, searchSourceIsLoading, retryCount]);

  const handleRetrySearch = () => {
    setRetryCount((count) => count + 1);
  };

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetInputValue(e.target.value);
    setSearchDisplayValue(e.target.value);
  };

  const handleRemoveSelectedSearch = () => {
    removeSelectedSearch();
    analytics.track(AnalyticsEnum.CLICK_CLEAR_FILTERS_SEARCH_MODAL);
  };

  return {
    inputValue,
    searchDisplayValue,
    handleSearchInput,
    allResults,
    shouldShowSearchResults,
    shouldShowEmptyState,
    shouldShowErrorState,
    handleRetrySearch,
    searchResultIsLoading,
    handleRemoveSelectedSearch,
    newSearchModalDesign,
  };
}
